import type { ReactElement, SVGProps } from 'react';

/**
 * Line-icon primitive (design-language §4): one coherent set, 1.5px stroke, monochrome,
 * inherits `currentColor` (→ brass on active via text colour). Replaces every emoji glyph.
 *
 * Geometry on a 24px viewBox, rounded caps/joins (Lucide/Phosphor-grade). Add a glyph by adding
 * a key to PATHS; render with <Icon name="mic" />. Decorative by default (aria-hidden); pass a
 * `title` to expose an accessible label.
 */

export type IconName =
  | 'mic'
  | 'micOff'
  | 'pause'
  | 'play'
  | 'keyboard'
  | 'globe'
  | 'idCard'
  | 'camera'
  | 'send'
  | 'x'
  | 'flag'
  | 'check'
  | 'download'
  | 'arrowUpRight'
  | 'info'
  | 'sun'
  | 'moon'
  | 'chevronDown'
  | 'star'
  | 'sparkle'
  | 'search'
  | 'image'
  | 'plus'
  | 'dot'
  | 'home'
  | 'grid'
  | 'calendar'
  | 'users'
  | 'chart'
  | 'package'
  | 'gauge'
  | 'cpu'
  | 'shield'
  | 'sliders'
  | 'bell'
  | 'message'
  | 'menu'
  | 'chevronRight'
  | 'logout'
  | 'clock'
  | 'timer'
  | 'qrCode'
  | 'trendingUp'
  | 'messageSquare'
  | 'skipForward'
  | 'refresh'
  | 'heart'
  | 'swatchPalette'
  | 'chevronUp'
  | 'copy'
  | 'edit'
  | 'trash'
  | 'flask'
  | 'gitBranch'
  | 'activity'
  | 'alertTriangle'
  | 'waveform'
  | 'radio'
  | 'candle';

/** Each entry is the inner markup for a 24×24 stroked icon (stroke set on the parent <svg>). */
const PATHS: Record<IconName, ReactElement> = {
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </>
  ),
  micOff: (
    <>
      <path d="M9 9v2a3 3 0 0 0 4.6 2.5M15 11V6a3 3 0 0 0-5.9-.7" />
      <path d="M5 11a7 7 0 0 0 10.3 6.2M12 18v3M4 3l16 16" />
    </>
  ),
  pause: (
    <>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </>
  ),
  play: <path d="M7 5.5v13l11-6.5z" />,
  keyboard: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M7 10h.01M11 10h.01M15 10h.01M8 14h8" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
    </>
  ),
  idCard: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="11" r="2" />
      <path d="M5.5 16c.6-1.4 2-2 3-2s2.4.6 3 2M14 9.5h4M14 13h3" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  send: <path d="M5 12 20 5l-4.5 15-3.5-6.5L5 12z" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  flag: <path d="M6 21V4M6 4h11l-2 3.5L17 11H6" />,
  check: <path d="M5 12.5 10 17l9-10" />,
  download: <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 19h14" />,
  arrowUpRight: <path d="M7 17 17 7m0 0H8m9 0v9" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z" />,
  chevronDown: <path d="M6 9.5 12 15l6-5.5" />,
  star: <path d="M12 4l2.4 5 5.6.7-4 3.8 1 5.5-5-2.8-5 2.8 1-5.5-4-3.8 5.6-.7z" />,
  sparkle: (
    <path d="M12 3c.4 3.6 1.4 4.6 5 5-3.6.4-4.6 1.4-5 5-.4-3.6-1.4-4.6-5-5 3.6-.4 4.6-1.4 5-5zM18.5 13c.2 1.6.6 2 2.5 2.3-1.9.3-2.3.7-2.5 2.3-.2-1.6-.6-2-2.5-2.3 1.9-.3 2.3-.7 2.5-2.3z" />
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M21 16l-5-5-9 9" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  dot: <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />,
  home: <path d="M4 11.5 12 4l8 7.5M6 10v9h12v-9M10 19v-5h4v5" />,
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.2" />
      <rect x="13" y="4" width="7" height="7" rx="1.2" />
      <rect x="4" y="13" width="7" height="7" rx="1.2" />
      <rect x="13" y="13" width="7" height="7" rx="1.2" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M4 9h16M8 3v4M16 3v4" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c.6-3 3-4.5 6-4.5s5.4 1.5 6 4.5M16 5.2a3 3 0 0 1 0 5.6M18 15.4c1.8.5 3.2 1.8 3.6 4.1" />
    </>
  ),
  chart: <path d="M4 20V4M4 20h16M8 17v-5M12 17V8M16 17v-7" />,
  package: (
    <>
      <path d="M12 3 20 7.5v9L12 21 4 16.5v-9z" />
      <path d="M4 7.5 12 12l8-4.5M12 12v9" />
    </>
  ),
  gauge: (
    <>
      <path d="M4 19a8 8 0 1 1 16 0" />
      <path d="M12 19l4-5" />
    </>
  ),
  cpu: (
    <>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
      <path d="M10 4v3M14 4v3M10 17v3M14 17v3M4 10h3M4 14h3M17 10h3M17 14h3" />
    </>
  ),
  shield: <path d="M12 3 5 6v6c0 4 3 6.5 7 9 4-2.5 7-5 7-9V6z" />,
  sliders: <path d="M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M8 14v6" />,
  bell: <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2.5h-15zM10 19a2 2 0 0 0 4 0" />,
  message: <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4 3.5V6a1 1 0 0 1 1-1z" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  chevronRight: <path d="M9.5 6 15 12l-5.5 6" />,
  logout: <path d="M9 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h3M15 8l4 4-4 4M19 12H9" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </>
  ),
  timer: (
    <>
      <path d="M10 2h4M12 14l4-4M12 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" />
    </>
  ),
  qrCode: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1" />
      <rect x="5" y="5" width="4" height="4" />
      <rect x="13" y="3" width="8" height="8" rx="1" />
      <rect x="15" y="5" width="4" height="4" />
      <rect x="3" y="13" width="8" height="8" rx="1" />
      <rect x="5" y="15" width="4" height="4" />
      <path d="M13 13h2v2h-2zM17 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM17 17h2v2h-2zM19 19h2v2h-2z" />
    </>
  ),
  trendingUp: <path d="M3 17l6-6 4 4 8-8M17 7h4v4" />,
  messageSquare: (
    <>
      <rect x="3" y="3" width="18" height="14" rx="2" />
      <path d="M3 21l4-4" />
    </>
  ),
  skipForward: <path d="M5 4l10 8-10 8V4zM19 5v14" />,
  refresh: <path d="M1 4v6h6M23 20v-6h-6M20.5 9A9 9 0 1 0 21 12" />,
  heart: (
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z" />
  ),
  swatchPalette: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="8" r="1.5" />
      <circle cx="15.5" cy="14" r="1.5" />
      <circle cx="8.5" cy="14" r="1.5" />
    </>
  ),
  chevronUp: <path d="M6 14.5 12 9l6 5.5" />,
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  edit: (
    <>
      <path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5" />
      <path d="M17.5 3.5a2 2 0 0 1 3 3l-9.5 9.5-4 1 1-4z" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M6 7l1 13h10l1-13M10 11v6M14 11v6" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </>
  ),
  flask: (
    <>
      <path d="M9 3h6M8 3l-4 14a1 1 0 0 0 .9 1.4h14.2A1 1 0 0 0 20 17L16 3" />
      <path d="M6.5 14h11" />
    </>
  ),
  gitBranch: (
    <>
      <circle cx="6" cy="18" r="3" />
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="6" r="3" />
      <path d="M6 9v6M18 9a6 6 0 0 1-6 6H9" />
    </>
  ),
  activity: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  alertTriangle: (
    <>
      <path d="M12 9v5M12 18h.01" />
      <path d="M10.3 4L2 19h20L13.7 4a2 2 0 0 0-3.4 0z" />
    </>
  ),
  waveform: <path d="M2 12h2M6 8v8M10 5v14M14 9v6M18 7v10M22 12h2" />,
  radio: (
    <>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6.4 6.4a8 8 0 1 0 11.2 11.2M6.4 17.6a8 8 0 0 1 0-11.2" />
    </>
  ),
  // R29 X5 — hurricane lamp / pillar candle: a rounded-glass hurricane silhouette with a wick +
  // teardrop flame. Same 1.5px stroke / rounded-cap language as the set (Lucide/Phosphor-grade).
  candle: (
    <>
      <path d="M12 3c1.6 1.4 2.4 2.7 2.4 4a2.4 2.4 0 0 1-4.8 0c0-1.3.8-2.6 2.4-4z" />
      <path d="M12 11v2" />
      <path d="M8 13h8v6a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2z" />
    </>
  ),
};

export function Icon({
  name,
  size = 20,
  title,
  className,
  strokeWidth = 1.5,
  ...rest
}: {
  name: IconName;
  size?: number;
  title?: string;
  strokeWidth?: number;
} & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={className}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
